import { useNavigate } from 'react-router-dom';
import { Security, Speed, Support, ThumbUp, Calculate } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { ExpandMore } from '@mui/icons-material';
import { useState, useEffect, useRef } from 'react';
import MortgageQuiz from '../../components/MortgageQuiz';
import {
  HomeWrapper,
  HeroSection,
  HeroImage,
  HeroContent,
  MainTitle,
  RotatingText,
  ButtonGroup,
  CTAButton,
  SecondaryButton,
  FeaturesSection,
  FeatureGrid,
  FeatureCard,
  FeatureTitle,
  FeatureDescription,
  StatsSection,
  StatsGrid,
  StatItem,
  TestimonialsSection,
  SectionTitle,
  TestimonialsGrid,
  TestimonialCard,
  TestimonialText,
  TestimonialAuthor,
  AuthorImage,
  AuthorInfo,
  FAQSection,
  FAQContainer,
  FAQItem,
  FAQQuestion,
  FAQAnswer,
  CaseStudiesSection,
  CaseStudiesGrid,
  CaseStudyCard,
  CaseStudyImage,
  CaseStudyContent,
  CaseStudyTitle,
  SavingsBadge,
  TrustBarSection,
  TrustLogosTrack,
  TrustLogo
} from './styles';

const testimonials = [
  {
    id: 1,
    text: "אפיק עזרה לנו להשיג תנאי משכנתא מעולים. החיסכון הכספי היה משמעותי והשירות היה מקצועי ואדיב.",
    author: "דניאל כהן",
    role: "רכישת דירה ראשונה",
    image: "/images/testimonials/1.jpg"
  },
  {
    id: 2,
    text: "הליווי המקצועי של אפיק היה מעל ומעבר למצופה. קיבלנו ייעוץ מדויק שחסך לנו המון כסף במיחזור המשכנתא.",
    author: "רונית לוי",
    role: "מיחזור משכנתא",
    image: "/images/testimonials/2.jpg"
  },
  {
    id: 3,
    text: "תודה רבה על השירות המסור והמקצועי. אפיק הייתה זמינה לכל שאלה ועזרה לנו להבין את כל התהליך.",
    author: "משה ישראלי",
    role: "משפר דיור",
    image: "/images/testimonials/3.jpg"
  }
];

const faqs = [
  {
    id: 1,
    question: "מתי כדאי לקחת ייעוץ משכנתא?",
    answer: "מומלץ לקחת ייעוץ משכנתא לפני כל החלטה משמעותית הקשורה למשכנתא - בין אם מדובר ברכישת נכס חדש, מיחזור משכנתא קיימת או שיפור תנאים. ייעוץ מקצועי יכול לחסוך לכם כסף רב לאורך זמן."
  },
  {
    id: 2,
    question: "כמה זמן לוקח תהליך לקיחת משכנתא?",
    answer: "תהליך לקיחת משכנתא יכול לקחת בין שבועיים לחודש, תלוי במורכבות המקרה ובמהירות השגת המסמכים הנדרשים. אנחנו מלווים אתכם לאורך כל התהליך ומסייעים בהאצתו ככל הניתן."
  },
  {
    id: 3,
    question: "האם אפשר למחזר משכנתא קיימת?",
    answer: "כן, בהחלט! מיחזור משכנתא הוא כלי חשוב שיכול לחסוך לכם אלפי שקלים בשנה. אנחנו בודקים את תנאי המשכנתא הקיימת שלכם ומסייעים במציאת מסלול טוב יותר."
  }
];

const caseStudies = [
  {
    id: 1,
    title: "מיחזור משכנתא למשפחת כהן",
    savings: "חיסכון של 240,000 ₪",
    description: "שיפרנו את תנאי הריבית וקיצרנו את תקופת המשכנתא ב-5 שנים ללא שינוי בהחזר החודשי.",
    icon: "🏠"
  },
  {
    id: 2,
    title: "משכנתא ראשונה לזוג צעיר",
    savings: "חיסכון של 180,000 ₪",
    description: "בנינו תמהיל מאוזן שמותאם ליכולת ההחזר העתידית שלהם והשגנו ריביות נמוכות משמעותית מהצעת הבנק המקורית.",
    icon: "🔑"
  },
  {
    id: 3,
    title: "איחוד הלוואות למשפחת לוי",
    savings: "הפחתה של 3,500 ₪ בהחזר החודשי",
    description: "איחדנו את כל ההלוואות הקצרות והיקרות לתוך המשכנתא, מה שאפשר למשפחה לנשום לרווחה כלכלית.",
    icon: "💰"
  }
];

const banks = ["בנק לאומי", "בנק הפועלים", "בנק מזרחי טפחות", "בנק דיסקונט", "הבנק הבינלאומי", "בנק ירושלים", "בנק יהב", "בנק מסד"];

const Home = () => {
  const navigate = useNavigate();
  const [openFAQ, setOpenFAQ] = useState(null);
  const [currentService, setCurrentService] = useState(0);
  const statsRef = useRef(null);

  const services = [
    'משכנתא חדשה - ליווי מקצועי בתהליך הראשון שלכם',
    'מחזור משכנתא - חיסכון משמעותי בתשלומים החודשיים',
    'משכנתא למסורבים - פתרונות יצירתיים ומותאמים',
    'איחוד הלוואות - הפחתת העומס הכלכלי',
    'משכנתא הפוכה - פתרון ייחודי לגיל השלישי',
    'גרירת משכנתא - שמירה על תנאים טובים',
    'ייעוץ לכלכלת המשפחה - תכנון פיננסי מקיף',
    'ייעוץ אשראי - שיפור והתאמת פתרונות'
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentService((prev) => (prev + 1) % services.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [services.length]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.stat-item').forEach((item, index) => {
              setTimeout(() => {
                item.classList.add('visible');
              }, index * 200);
            });
          }
        });
      },
      { threshold: 0.3 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <HomeWrapper>
        <HeroSection>
          <HeroContent>
            <MainTitle
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span>אפיק לב</span>
              ייעוץ משכנתאות מקצועי
            </MainTitle>
            
            <RotatingText>
              <motion.span
                key={currentService}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
              >
                {services[currentService]}
              </motion.span>
            </RotatingText>

            <ButtonGroup>
              <CTAButton
                onClick={() => navigate('/contact')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                לייעוץ ראשוני
              </CTAButton>
              <SecondaryButton
                onClick={() => navigate('/calculator')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Calculate />
                מחשבון משכנתא
              </SecondaryButton>
            </ButtonGroup>
          </HeroContent>

          <HeroImage 
            src="/images/afik-profile.png"
            alt="אפיק לב - יועצת משכנתאות"
          />
        </HeroSection>

        <FeaturesSection>
          <FeatureGrid>
            <FeatureCard>
              <Security />
              <FeatureTitle>מקצועיות ואמינות</FeatureTitle>
              <FeatureDescription>
                ליווי מקצועי ואמין לאורך כל הדרך, עם דגש על שקיפות מלאה והתאמה אישית.
              </FeatureDescription>
            </FeatureCard>

            <FeatureCard>
              <Speed />
              <FeatureTitle>חיסכון בזמן וכסף</FeatureTitle>
              <FeatureDescription>
                השוואת הצעות מכל הבנקים במקום אחד, תוך חיסכון משמעותי בעלויות ובזמן.
              </FeatureDescription>
            </FeatureCard>

            <FeatureCard>
              <Support />
              <FeatureTitle>ליווי אישי צמוד</FeatureTitle>
              <FeatureDescription>
                תמיכה וליווי מלא בכל שלב, מהפגישה הראשונה ועד לקבלת המשכנתא.
              </FeatureDescription>
            </FeatureCard>

            <FeatureCard>
              <ThumbUp />
              <FeatureTitle>תנאים מועדפים</FeatureTitle>
              <FeatureDescription>
                ניצול הקשרים והניסיון שלנו להשגת התנאים הטובים ביותר עבורכם.
              </FeatureDescription>
            </FeatureCard>
          </FeatureGrid>
        </FeaturesSection>

        <StatsSection ref={statsRef}>
          <StatsGrid>
            <StatItem className="stat-item">
              <h2>1000+</h2>
              <p>לקוחות מרוצים</p>
            </StatItem>
            
            <StatItem className="stat-item">
              <h2>7+</h2>
              <p>שנות ניסיון</p>
            </StatItem>
            
            <StatItem className="stat-item">
              <h2>500K</h2>
              <p>חיסכון ממוצע ללקוח</p>
            </StatItem>
            
            <StatItem className="stat-item">
              <h2>100%</h2>
              <p>שביעות רצון</p>
            </StatItem>
          </StatsGrid>
        </StatsSection>

        <TrustBarSection>
          <TrustLogosTrack>
            {[...banks, ...banks].map((bank, index) => (
              <TrustLogo key={index}>{bank}</TrustLogo>
            ))}
          </TrustLogosTrack>
        </TrustBarSection>

        <CaseStudiesSection>
          <SectionTitle>סיפורי הצלחה</SectionTitle>
          <CaseStudiesGrid>
            {caseStudies.map((study, index) => (
              <CaseStudyCard
                key={study.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <CaseStudyImage>{study.icon}</CaseStudyImage>
                <CaseStudyContent>
                  <SavingsBadge>{study.savings}</SavingsBadge>
                  <CaseStudyTitle>{study.title}</CaseStudyTitle>
                  <p style={{ color: '#666', lineHeight: '1.6' }}>{study.description}</p>
                </CaseStudyContent>
              </CaseStudyCard>
            ))}
          </CaseStudiesGrid>
        </CaseStudiesSection>

        <TestimonialsSection>
          <SectionTitle>מה הלקוחות אומרים</SectionTitle>
          <TestimonialsGrid>
            {testimonials.map(testimonial => (
              <TestimonialCard key={testimonial.id}>
                <TestimonialText>{testimonial.text}</TestimonialText>
                <TestimonialAuthor>
                  <AuthorImage src={testimonial.image} alt={testimonial.author} />
                  <AuthorInfo>
                    <h4>{testimonial.author}</h4>
                    <p>{testimonial.role}</p>
                  </AuthorInfo>
                </TestimonialAuthor>
              </TestimonialCard>
            ))}
          </TestimonialsGrid>
        </TestimonialsSection>

        <section id="quiz">
          <SectionTitle>בדקו זכאות למשכנתא</SectionTitle>
          <p style={{ textAlign: 'center', color: '#666', marginBottom: '2rem' }}>
            ענו על מספר שאלות קצרות וקבלו הערכה ראשונית מהמומחים שלנו
          </p>
          <MortgageQuiz />
        </section>

        <FAQSection>
          <SectionTitle>שאלות נפוצות</SectionTitle>
          <FAQContainer>
            {faqs.map(faq => (
              <FAQItem key={faq.id} onClick={() => setOpenFAQ(openFAQ === faq.id ? null : faq.id)}>
                <FAQQuestion isOpen={openFAQ === faq.id}>
                  {faq.question}
                  <ExpandMore />
                </FAQQuestion>
                <FAQAnswer isOpen={openFAQ === faq.id}>
                  {faq.answer}
                </FAQAnswer>
              </FAQItem>
            ))}
          </FAQContainer>
        </FAQSection>
      </HomeWrapper>
    </motion.div>
  );
};

export default Home; 